'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import { HomeGallery } from '@/components/home-gallery'

export default function LandingPage() {
  const { data: session } = useSession()
  const [compact, setCompact] = useState(false)

  useEffect(() => {
    const checkSize = () => {
      const vh = window.innerHeight
      const vw = window.innerWidth
      // Compact mode for short screens, but not if width >= 640 and height >= 600
      // - Height >= 700: always large
      // - Height 600-699: large if width >= 640, compact if narrow
      // - Height < 600: always compact
      const isCompact = vh < 600 || (vh < 700 && vw < 640)
      setCompact(isCompact)
    }
    checkSize()
    window.addEventListener('resize', checkSize)
    return () => window.removeEventListener('resize', checkSize)
  }, [])

  return (
    <div className="flex flex-col grow w-full overflow-hidden">
      {/* Main content area - centers vertically when there's room */}
      <div className="flex flex-col grow items-center justify-center py-4 overflow-auto">
        <motion.div
          className="max-w-3xl mx-auto px-4 md:px-8 w-full flex flex-col justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Mr Yarn - Bouncing Logo */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className={`flex justify-center ${compact ? 'mb-2' : 'mb-4'}`}
          >
            <motion.img
              src="/images/pattern-bot-trans-750-750.webp"
              alt="Mr Yarn"
              className="drop-shadow-md"
              style={{ width: compact ? 80 : 110, display: 'inline-flex' }}
              animate={{
                y: compact
                  ? [0, 10, 0, -10, -20, -30, -20, -10, 0, -16, 0, -8, 0]
                  : [0, 16, 0, -15, -30, -45, -30, -15, 0, -24, 0, -12, 0],
                scaleX: [
                  1, 1.06, 1, 0.99, 0.98, 0.98, 0.98, 0.99, 1.05, 0.99, 1.03, 0.995,
                  1,
                ],
                scaleY: [
                  1, 0.94, 1, 1.01, 1.015, 1.02, 1.015, 1.01, 0.94, 1.01, 0.97,
                  1.005, 1,
                ],
              }}
              transition={{
                delay: 2,
                duration: 1.5,
                times: [
                  0, 0.12, 0.18, 0.23, 0.28, 0.33, 0.38, 0.43, 0.58, 0.72, 0.82,
                  0.91, 1,
                ],
                ease: [
                  'easeIn',
                  'easeOut',
                  'easeOut',
                  'easeOut',
                  'easeOut',
                  'easeIn',
                  'easeIn',
                  'easeIn',
                  'easeOut',
                  'easeIn',
                  'easeOut',
                  'easeIn',
                ],
                repeat: Infinity,
                repeatDelay: 10,
              }}
            />
          </motion.div>

          <div className={`w-fit ${compact ? 'py-2 px-3' : 'py-4 px-4'} m-auto bg-[#fffffff0] rounded-xl border-2 border-[var(--rowmantic-red)] shadow-md shadow-black/40`}>
            {/* Rowmantic Logo */}
            <motion.div className={`flex justify-center ${compact ? 'mb-2' : 'mb-4'} w-fit mx-auto`}>
              <motion.img
                src="/images/rowmantic-logo-trans.png"
                alt="rowmantic.com logo"
                className={`${compact ? 'w-[120px]' : 'w-[167px] tall:w-[200px]'} max-w-full`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              />
            </motion.div>

            {/* Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className={`${compact ? 'text-base' : 'text-xl tall:text-2xl'} text-center leading-[1.3] ${compact ? 'mb-1' : 'mb-2'} font-narrow w-fit mx-auto`}
              style={{ color: 'var(--rowmantic-red)' }}
            >
              <span className="sm:hidden">
                Love finding beautiful <br /> knit and crochet patterns!
              </span>
              <span className="hidden sm:inline">
                Love finding beautiful knit and crochet patterns!
              </span>
            </motion.div>

            {/* Description Paragraph */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65 }}
              className={`${compact ? 'text-sm' : 'text-base tall:text-lg'} text-zinc-600 dark:text-zinc-400 text-center ${compact ? 'mb-1' : 'mb-2'} max-w-md mx-auto`}
            >
              <p className={`${compact ? 'py-1' : 'py-2'} leading-[1.4]`}>
                <span className="sm:hidden">
                  Rowmantic uses AI to help you explore
                  <br />
                  the extraordinary collection of
                  <br />
                  knit and crochet patterns
                  <br />
                  on Ravelry.com.
                </span>
                <span className="hidden sm:inline">
                  Rowmantic uses AI to help you explore the extraordinary collection of knit and crochet patterns on Ravelry.com.
                </span>
              </p>
              <p className={`${compact ? 'py-1' : 'py-2'} leading-[1.4]`}>
                <span className="sm:hidden">
                  Rowmantic is now in limited trial. <br />
                  Check back for more information.
                </span>
                <span className="hidden sm:inline">
                  Rowmantic is now in limited trial. Check back for more information.
                </span>
              </p>
            </motion.div>

            {/* Login/Chat Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className={`flex flex-col sm:flex-row gap-2 justify-center items-center max-w-md mx-auto w-fit mx-auto ${compact ? 'text-sm' : 'tall:text-lg'}`}
            >
              {session ? (
                // Authenticated: Show "New Chat" button
                <Link
                  href="/chat"
                  className={`sm:w-auto ${compact ? 'px-6 py-1.5' : 'px-8 py-2 tall:px-10 tall:py-2.5'} bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-center font-medium w-fit mx-auto`}
                >
                  New Chat
                </Link>
              ) : (
                // Not authenticated: Show "Guest Login" button
                <>
                  {/* <Link
                    href="/login"
                    className=" w-fit mx-auto sm:w-auto px-8 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-center font-medium"
                  >
                    Account Login
                  </Link> */}
                  <Link
                    href="/guest-login"
                    className={`w-full sm:w-auto ${compact ? 'px-6 py-1.5' : 'px-8 py-2 tall:px-10 tall:py-2.5'} border border-gray-300 dark:border-zinc-700 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors text-center font-medium`}
                  >
                    Guest Login
                  </Link>
                </>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Gallery at the bottom - fades in after main content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="shrink-0 pb-4"
      >
        <HomeGallery />
      </motion.div>
    </div>
  )
}
